import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/Logo'

/* ─── Share button ──────────────────────────────────────────────── */

function ShareButton({ className }: { className?: string }) {
  const [toast, setToast] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function showToast() {
    setToast(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setToast(false), 2200)
  }

  async function handleShare() {
    try {
      await navigator.share({ title: document.title, url: window.location.href })
    } catch {
      try {
        await navigator.clipboard.writeText(window.location.href)
        showToast()
      } catch {
        // clipboard unavailable — do nothing
      }
    }
  }

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={handleShare}
        className={className}
      >
        Share this page
      </button>
      {toast && (
        <span className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-ovation-ink px-3 py-1 text-xs text-ovation-paper shadow-lg">
          Link copied
        </span>
      )}
    </span>
  )
}

/* ─── Header ────────────────────────────────────────────────────── */

function Header() {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-ovation-paper/90 backdrop-blur-md border-b border-ovation-ink/8 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" aria-label="Ovation home">
          <Logo showWordmark={false} className="text-ovation-ink" />
          <span className="sr-only">Ovation</span>
        </Link>
        <span className="font-display text-2xl tracking-tight text-ovation-ink select-none">Ovation</span>
        <nav className="flex items-center gap-6">
          <Link
            to="/download"
            className="text-sm text-ovation-ink/70 hover:text-ovation-ink transition-colors"
          >
            Download app
          </Link>
          <Link
            to="/login"
            className="text-sm text-ovation-ink/70 hover:text-ovation-ink transition-colors"
          >
            Log in
          </Link>
          <Link
            to="/join/artist"
            className="rounded-none border border-ovation-ink bg-ovation-ink px-5 py-2.5 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/85"
          >
            Get started
          </Link>
        </nav>
      </div>
    </header>
  )
}

/* ─── Hero ──────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="min-h-screen bg-ovation-paper pt-24 pb-20">
      <div className="mx-auto max-w-7xl px-6">
        {/* Three-column hero */}
        <div className="grid grid-cols-1 gap-0 lg:grid-cols-3 lg:gap-px lg:border lg:border-ovation-ink/12">
          {/* Col 1 — managers */}
          <div className="flex flex-col justify-between border border-ovation-ink/12 p-8 lg:border-0 lg:p-10 xl:pr-14 xl:pb-14 xl:pt-14">
            <div>
              <span className="inline-block mb-5 text-xs uppercase tracking-[0.22em] text-ovation-accent font-medium">
                For artist managers
              </span>
              <h1 className="font-display text-4xl leading-[1.05] tracking-tight text-ovation-ink lg:text-4xl xl:text-5xl">
                The intelligent back office for the artists you represent.
              </h1>
              <p className="mt-6 text-sm leading-relaxed text-ovation-muted max-w-sm">
                Manage multi-artist rosters from a single dashboard. Pipeline every deal,
                territory map every market, track every broker — all tied to the same
                source of truth.
              </p>
            </div>
            <div className="mt-10">
              <Link
                to="/join/artist"
                className="inline-flex items-center gap-2 border border-ovation-ink bg-ovation-ink px-6 py-3.5 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/85"
              >
                Book a demo
                <ArrowRight />
              </Link>
            </div>
          </div>

          {/* Col 2 — solo artists */}
          <div className="flex flex-col justify-between border border-ovation-ink/12 border-t-0 p-8 bg-ovation-ink lg:border-0 lg:bg-ovation-paper lg:border-l lg:border-r lg:border-ovation-ink/12 lg:p-10 xl:p-14">
            <div>
              <span className="inline-block mb-5 text-xs uppercase tracking-[0.22em] text-ovation-accent font-medium">
                For solo artists
              </span>
              <h2 className="font-display text-4xl leading-[1.05] tracking-tight text-ovation-paper lg:text-ovation-ink lg:text-4xl xl:text-5xl">
                Run your touring career like the business it is.
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-ovation-paper/70 lg:text-ovation-muted max-w-sm">
                Stop managing shows from a spreadsheet. Ovation gives you the full
                operations stack — pipeline, travel, ticketing, contracts — built for
                the artist who runs their own business.
              </p>
            </div>
            <div className="mt-10">
              <Link
                to="/join/artist"
                className="inline-flex items-center gap-2 border border-ovation-paper bg-ovation-paper px-6 py-3.5 text-sm font-medium text-ovation-ink transition-colors hover:bg-ovation-paper/90 lg:border-ovation-ink lg:bg-ovation-ink lg:text-ovation-paper lg:hover:bg-ovation-ink/85"
              >
                Start your free trial
                <ArrowRight className="lg:text-ovation-paper" />
              </Link>
            </div>
          </div>

          {/* Col 3 — crew & tour staff */}
          <div className="flex flex-col justify-between border border-ovation-ink/12 border-t-0 p-8 bg-ovation-ink lg:border-0 lg:p-10 xl:pl-14 xl:pb-14 xl:pt-14">
            <div>
              <span className="inline-block mb-5 text-xs uppercase tracking-[0.22em] text-ovation-accent font-medium">
                For crew &amp; tour staff
              </span>
              <h2 className="font-display text-4xl leading-[1.05] tracking-tight text-ovation-paper lg:text-4xl xl:text-5xl">
                Get discovered by the artists you want to work with.
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-ovation-paper/70 max-w-sm">
                Free forever. Create a profile, list your skills and rates, manage your
                availability. Managers find you when they're casting their next tour.
              </p>
            </div>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/join"
                className="inline-flex items-center gap-2 border border-ovation-paper bg-transparent px-6 py-3.5 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-paper/10"
              >
                Create your profile
                <ArrowRight />
              </Link>
              <Link
                to="/download"
                className="inline-flex items-center gap-2 border border-ovation-paper/40 px-6 py-3.5 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-paper/10"
              >
                Get the mobile app
              </Link>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p className="mt-16 text-center font-display text-2xl italic tracking-wide text-ovation-ink/50 lg:text-3xl">
          "The preparation is ours. The ovation is yours."
        </p>
      </div>
    </section>
  )
}

/* ─── Features ──────────────────────────────────────────────────── */

const FEATURES = [
  {
    n: '01',
    name: 'Event Pipeline',
    desc: 'From inquiry to confirmed show, one source of truth.',
  },
  {
    n: '02',
    name: 'Territory Intelligence',
    desc: 'Map opportunity. Plan tours climate-smart.',
  },
  {
    n: '03',
    name: 'Broker Performance',
    desc: "Know who's closing deals and who's dragging them.",
  },
  {
    n: '04',
    name: 'Direct Ticketing',
    desc: 'Sell direct via Hive Cells. Your fans, your margins.',
  },
  {
    n: '05',
    name: 'Travel & Logistics',
    desc: 'Party manifests, route optimisation, carnet tracking.',
  },
  {
    n: '06',
    name: 'AI Assistant',
    desc: 'Ask anything. Answers from your own data.',
  },
] as const

function Features() {
  return (
    <section id="features" className="bg-ovation-paper py-28 border-t border-ovation-ink/10">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="font-display text-4xl tracking-tight text-ovation-ink lg:text-5xl">
          Everything the tour demands.
        </h2>
        <p className="mt-4 text-ovation-muted max-w-xl">
          Six core modules, one platform — built specifically for the business of
          touring live music.
        </p>

        <div className="mt-16 grid grid-cols-1 gap-px border border-ovation-ink/12 bg-ovation-ink/12 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.n} className="flex flex-col bg-ovation-paper p-8">
              <span className="font-display text-4xl text-ovation-accent leading-none mb-4">
                {f.n}
              </span>
              <h3 className="font-display text-2xl tracking-tight text-ovation-ink">
                {f.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ovation-muted">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── How It Works ──────────────────────────────────────────────── */

const STEPS = [
  {
    n: '1',
    title: 'Connect your inbox',
    body: 'Every booking inquiry — email, referral, direct — lands in Ovation the moment it arrives. Nothing falls through the cracks.',
  },
  {
    n: '2',
    title: 'AI triage runs in parallel',
    body: 'Claude classifies each lead, runs due diligence on the promoter, checks for duplicates, and dispatches an EPK — all before you open the email.',
  },
  {
    n: '3',
    title: 'Pipeline tracks every deal',
    body: 'From first inquiry to post-show settlement, every stage is visible. Brokers see their data. Management sees everything.',
  },
  {
    n: '4',
    title: 'Post-show intelligence',
    body: 'Debrief, reconcile, learn. Structured show notes feed the next booking decision in the same market or venue.',
  },
] as const

function HowItWorks() {
  return (
    <section className="bg-ovation-ink py-28">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="font-display text-4xl tracking-tight text-ovation-paper lg:text-5xl">
          How it works.
        </h2>

        <div className="mt-20 flex flex-col gap-0">
          {STEPS.map((step, i) => {
            const isRight = i % 2 === 1
            return (
              <div
                key={step.n}
                className={`flex flex-col gap-8 border-t border-ovation-paper/10 py-16 lg:flex-row lg:items-start lg:gap-20 ${
                  isRight ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="lg:w-1/4 shrink-0">
                  <span className="font-display text-8xl leading-none text-ovation-accent/30">
                    {step.n}
                  </span>
                </div>
                <div className="lg:w-3/4 max-w-2xl">
                  <h3 className="font-display text-3xl tracking-tight text-ovation-paper">
                    {step.title}
                  </h3>
                  <p className="mt-5 text-base leading-relaxed text-ovation-paper/60">
                    {step.body}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ─── Pricing ───────────────────────────────────────────────────── */

const PLANS = [
  {
    name: 'Solo',
    price: '£299',
    period: '/mo',
    desc: 'Pipeline, territory, email, and up to 5 users. Everything you need to run a solo touring career.',
    cta: 'Start free trial',
    href: '/join/artist',
    featured: false,
    crew: false,
  },
  {
    name: 'Management',
    price: '£799',
    period: '/mo',
    desc: 'Everything in Solo, plus ticketing, merch, analytics, and travel. Up to 15 users. Built for management companies.',
    cta: 'Start free trial',
    href: '/join/artist',
    featured: true,
    crew: false,
  },
  {
    name: 'Enterprise',
    price: '£1,999',
    period: '/mo',
    desc: 'Everything plus AI Assistant, royalties, contracts, climate guardian, and unlimited users. White-labelled for your brand.',
    cta: 'Book a demo',
    href: '/join/artist',
    featured: false,
    crew: false,
  },
] as const

const CREW_PLAN = {
  name: 'Crew',
  price: 'Free',
  period: 'forever',
  desc: 'Profile on the open talent marketplace. Availability calendar. Engagement inbox. Testimonials. Always free.',
  cta: 'Create free account',
  href: '/join',
} as const

function Pricing() {
  return (
    <section id="pricing" className="bg-ovation-paper py-28 border-t border-ovation-ink/10">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="font-display text-4xl tracking-tight text-ovation-ink lg:text-5xl">
          Pricing for everyone who makes live music happen.
        </h2>
        <p className="mt-4 text-ovation-muted max-w-xl">
          Paid plans billed monthly — no setup fees, cancel anytime. Crew accounts are always free.
        </p>

        <div className="mt-16 grid grid-cols-1 gap-px border border-ovation-ink/12 bg-ovation-ink/12 lg:grid-cols-4">
          {/* Crew card — free, visually distinct */}
          <div className="relative flex flex-col p-8 bg-ovation-paper text-ovation-ink border-2 border-ovation-accent lg:border-0 lg:border-r-2 lg:border-ovation-accent">
            <span className="absolute top-4 right-4 inline-block rounded-none bg-ovation-accent px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ovation-ink">
              FREE
            </span>
            <div className="font-display text-xl tracking-tight">{CREW_PLAN.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-5xl tracking-tight">{CREW_PLAN.price}</span>
              <span className="text-sm text-ovation-muted">{CREW_PLAN.period}</span>
            </div>
            <p className="mt-6 text-sm leading-relaxed flex-1 text-ovation-muted">
              {CREW_PLAN.desc}
            </p>
            <Link
              to={CREW_PLAN.href}
              className="mt-10 inline-block px-6 py-3.5 text-sm font-medium text-center transition-colors border border-ovation-accent bg-transparent text-ovation-ink hover:bg-ovation-accent/15"
            >
              {CREW_PLAN.cta}
            </Link>
          </div>

          {/* Paid plans */}
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`flex flex-col p-8 ${
                plan.featured
                  ? 'bg-ovation-ink text-ovation-paper'
                  : 'bg-ovation-paper text-ovation-ink'
              }`}
            >
              {plan.featured && (
                <span className="mb-4 inline-block text-xs uppercase tracking-[0.22em] text-ovation-accent font-medium">
                  Most popular
                </span>
              )}
              <div className="font-display text-xl tracking-tight">{plan.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl tracking-tight">{plan.price}</span>
                <span className={`text-sm ${plan.featured ? 'text-ovation-paper/50' : 'text-ovation-muted'}`}>
                  {plan.period}
                </span>
              </div>
              <p className={`mt-6 text-sm leading-relaxed flex-1 ${plan.featured ? 'text-ovation-paper/70' : 'text-ovation-muted'}`}>
                {plan.desc}
              </p>
              <Link
                to={plan.href}
                className={`mt-10 inline-block px-6 py-3.5 text-sm font-medium text-center transition-colors ${
                  plan.featured
                    ? 'border border-ovation-paper bg-ovation-paper text-ovation-ink hover:bg-ovation-paper/90'
                    : 'border border-ovation-ink bg-ovation-ink text-ovation-paper hover:bg-ovation-ink/85'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── CTA band ──────────────────────────────────────────────────── */

function CtaBand() {
  return (
    <section className="bg-ovation-accent py-24">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="font-display text-4xl tracking-tight text-ovation-ink lg:text-5xl xl:text-6xl">
          Ready to put the preparation behind you?
        </h2>
        <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to="/join/artist"
            className="inline-block border border-ovation-ink bg-ovation-ink px-8 py-4 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/85"
          >
            Start free trial
          </Link>
          <ShareButton
            className="inline-block border border-ovation-ink px-8 py-4 text-sm font-medium text-ovation-ink transition-colors hover:bg-ovation-ink/8"
          />
        </div>
        <p className="mt-8 text-sm text-ovation-ink/60 text-center">
          Managers, artists, and crew — everyone on the same page.{' '}
          <Link to="/join" className="underline underline-offset-2 hover:text-ovation-ink transition-colors">
            Crew? Join free — always.
          </Link>
        </p>
      </div>
    </section>
  )
}

/* ─── Footer ────────────────────────────────────────────────────── */

function Footer() {
  return (
    <footer className="bg-ovation-paper border-t border-ovation-ink/10">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          {/* Brand */}
          <div>
            <Logo />
            <p className="mt-4 text-xs text-ovation-muted max-w-xs leading-relaxed">
              The preparation is ours. The ovation is yours.
            </p>
          </div>

          {/* Links */}
          <nav className="flex flex-wrap gap-x-10 gap-y-4 text-sm text-ovation-ink/70">
            <a href="#features" className="hover:text-ovation-ink transition-colors">
              Features
            </a>
            <a href="#pricing" className="hover:text-ovation-ink transition-colors">
              Pricing
            </a>
            <Link to="/login" className="hover:text-ovation-ink transition-colors">
              Log in
            </Link>
            <Link to="/download" className="hover:text-ovation-ink transition-colors">
              Download app
            </Link>
            <a href="#" className="hover:text-ovation-ink transition-colors">
              Privacy
            </a>
            <a href="#" className="hover:text-ovation-ink transition-colors">
              Terms
            </a>
          </nav>
        </div>

        <div className="mt-12 border-t border-ovation-ink/8 pt-8 text-xs text-ovation-muted">
          © 2026 Geeky Bee Ltd. All rights reserved.
        </div>
      </div>
    </footer>
  )
}

/* ─── Tiny inline SVG ───────────────────────────────────────────── */

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  )
}

/* ─── Page ──────────────────────────────────────────────────────── */

/**
 * Ovation public homepage — editorial / magazine feel.
 * Shown to unauthenticated visitors; authenticated users are redirected
 * to /dashboard by HomepageGate in App.tsx.
 */
export function HomePage() {
  return (
    <div className="min-h-screen bg-ovation-paper">
      <Header />
      <main>
        <Hero />
        <Features />
        <HowItWorks />
        <Pricing />
        <CtaBand />
      </main>
      <Footer />
    </div>
  )
}
